import type { Translations } from '@/i18n/locales/en';
import type { CategorySchedule, OptionRules } from '@restaurant/api-contracts';

type Copy = Translations['menuQuickEdit'];

export function weekday(day: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, 7 + day)),
  );
}

function dayRanges(days: number[], locale: string): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const gap = sorted.findIndex((day, i) => i > 0 && day - (sorted[i - 1] ?? day) > 1);
  const wraps = sorted.length < 7 && sorted[0] === 0 && sorted.at(-1) === 6 && gap > 0;
  const ordered = wraps ? [...sorted.slice(gap), ...sorted.slice(0, gap)] : sorted;
  const runs: number[][] = [];
  for (const day of ordered) {
    const run = runs.at(-1);
    const last = run?.at(-1);
    if (run && last !== undefined && (day === last + 1 || (last === 6 && day === 0))) run.push(day);
    else runs.push([day]);
  }
  return runs
    .map((run) => {
      const first = run[0] ?? 0;
      const end = run.at(-1) ?? first;
      return first === end
        ? weekday(first, locale)
        : `${weekday(first, locale)}–${weekday(end, locale)}`;
    })
    .join(', ');
}

export function describeHours(
  schedule: CategorySchedule | null,
  locale: string,
  copy: Copy,
): string {
  if (!schedule || schedule.windows.length === 0) return copy.always;
  return schedule.windows
    .map((w) => `${dayRanges(w.days, locale)} ${w.startTime}–${w.endTime}`)
    .join('; ');
}

export function describeRules(rules: OptionRules, copy: Copy): string {
  const need = rules.isRequired ? copy.rules.required : copy.rules.optional;
  if (!rules.isMultiple) return `${need}, ${copy.rules.pickOne}`;
  const { minSelect: min, maxSelect: max } = rules;
  const pick =
    max === null
      ? min > 0
        ? copy.rules.pickAtLeast(min)
        : copy.rules.pickAny
      : min > 0
        ? copy.rules.pickRange(min, max)
        : copy.rules.pickUpTo(max);
  return `${need}, ${pick}`;
}
